// Reusable modal component
// Usage:
//   <button data-action="open-modal" data-modal="some-modal-id">Open</button>
//   <dialog id="some-modal-id" class="ap-modal" aria-labelledby="...">...</dialog>
// Close via: [data-action="close-modal"] inside the dialog, click on backdrop, or Esc.

import { qs, qsa, on, delegate, lockScroll, trapFocus } from '../lib/dom.js';

const releaseByModal = new WeakMap();

// Lazy iframes: an <iframe data-src="…"> inside a modal only loads when the
// modal opens (no background video/network on page load) and is unloaded on
// close so playback stops. Used by the location "Vezi pe YouTube" video popup.
function loadModalFrames(dlg) {
  qsa('iframe[data-src]', dlg).forEach((f) => {
    if (f.getAttribute('src') !== f.dataset.src) f.setAttribute('src', f.dataset.src);
  });
}
function unloadModalFrames(dlg) {
  qsa('iframe[data-src]', dlg).forEach((f) => { f.setAttribute('src', ''); });
}

function openModal(dlg) {
  if (!dlg) return;
  if (typeof dlg.showModal === 'function') dlg.showModal();
  else dlg.setAttribute('open', '');
  lockScroll(true);
  releaseByModal.set(dlg, trapFocus(dlg));
  loadModalFrames(dlg);
  dlg.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
}

function closeModal(dlg) {
  if (!dlg) return;
  if (typeof dlg.close === 'function') dlg.close();
  else dlg.removeAttribute('open');
  cleanup(dlg);
}

function cleanup(dlg) {
  lockScroll(false);
  const release = releaseByModal.get(dlg);
  if (release) { release(); releaseByModal.delete(dlg); }
  unloadModalFrames(dlg);
  dlg.dispatchEvent(new CustomEvent('modal:close', { bubbles: true }));
}

export function initModals() {
  // Open trigger (anywhere on the page)
  delegate(document, 'click', '[data-action="open-modal"]', (e, btn) => {
    e.preventDefault();
    const id = btn.dataset.modal;
    if (!id) return;
    const dlg = document.getElementById(id);
    // Record WHICH button opened the modal into the form's hidden
    // form_source field (lead attribution: banner / pill / tabbar / …).
    if (dlg && btn.dataset.modalSource) {
      const src = dlg.querySelector('[data-role="modal-source"]');
      if (src) src.value = btn.dataset.modalSource;
    }
    openModal(dlg);
  });

  // Close trigger inside any modal
  delegate(document, 'click', '[data-action="close-modal"]', (e, btn) => {
    e.preventDefault();
    const dlg = btn.closest('dialog.ap-modal');
    if (dlg) closeModal(dlg);
  });

  // Per-modal: backdrop click + ESC + native close event
  qsa('dialog.ap-modal').forEach((dlg) => {
    on(dlg, 'click', (e) => { if (e.target === dlg) closeModal(dlg); });
    on(dlg, 'cancel', (e) => { e.preventDefault(); closeModal(dlg); });
    on(dlg, 'close', () => cleanup(dlg));
  });

  // Auto-close any modal after a successful contact-form submit
  on(document, 'modal:auto-close', (e) => {
    const dlg = e.target.closest && e.target.closest('dialog.ap-modal');
    if (dlg) closeModal(dlg);
  });
}

// Programmatic API in case other modules need it
export { openModal, closeModal };
